/**
 * AttachmentDetailDrawer — single-record drawer for a document or
 * communication.
 *
 * No threading in this patch; each record shows on its own. Patch 2 adds the
 * multi-message thread drawer.
 *
 * Document variant: PDF/image preview inline (iframe / img), metadata,
 * download.
 * Communication variant: notes body + attached docs list, edit/delete CTAs.
 */
import { useMemo, useState } from 'react';
import { Download, Trash2, FileText, Phone, Calendar, Mail, MessageSquare, Send, ArrowDown, NotebookPen, MessageCircleReply } from 'lucide-react';
import { Drawer } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Alert } from '@/components/ui/alert';
import {
  useAttachment,
  useDeleteAttachment,
  useSupersedeChain,
  threadAttachmentsPdfUrl,
  useThread,
  type Attachment,
  type CommType,
  type DocumentType,
} from '@/lib/attachments-queries';
import { useCurrentUser } from '@/lib/auth';
import { formatDate, formatDateTime, formatRelative } from '@procurement/shared';
import { LogCommunicationModal } from './log-communication-modal';
import { buildThreadTree, type ThreadNode } from './thread-tree';

interface Props {
  attachmentId: string | null;
  onClose: () => void;
}

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  DFA: 'DFA',
  PO: 'Purchase order',
  SCHEDULE_OF_STORES: 'Schedule of Stores',
  SUPPLIER_INVOICE: 'Supplier invoice',
  CLIENT_INVOICE: 'Client invoice',
  BG_INSTRUMENT: 'BG instrument',
  INSPECTION_REPORT: 'Inspection report',
  LETTER: 'Letter',
  EMAIL_SCAN: 'Email scan',
  CHEQUE_COPY: 'Cheque copy',
  CERTIFICATE: 'Certificate',
  DRAWING: 'Drawing',
  AGREEMENT: 'Agreement',
  I_NOTE: 'I-Note',
  CRV: 'CRV',
  OTHER: 'Other',
};

const COMM_TYPE_LABELS: Record<CommType, string> = {
  PHONE_CALL: 'Phone call',
  MEETING: 'Meeting',
  LETTER_RECEIVED: 'Letter received',
  LETTER_SENT: 'Letter sent',
  EMAIL: 'Email',
  WHATSAPP: 'WhatsApp',
  SMS: 'SMS',
  INTERNAL_NOTE: 'Internal note',
};


function copiedDepartmentText(data: Attachment): string {
  return data.copiedDepartments.length > 0
    ? data.copiedDepartments.map((d) => d.name).join(', ')
    : '—';
}

function canIncludeInThreadPdf(doc: Attachment): boolean {
  const mime = doc.fileMimeType?.trim().toLowerCase();
  const name = doc.displayName?.trim().toLowerCase() ?? '';
  return (
    mime === 'application/pdf' ||
    mime === 'image/jpeg' ||
    mime === 'image/jpg' ||
    mime === 'image/png' ||
    name.endsWith('.pdf') ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.png')
  );
}

function ThreadPdfDownloadLink({ threadId, messages }: { threadId: string; messages: Array<{ attachedDocs: Attachment[] }> }) {
  const allDocs = messages.flatMap((m) => m.attachedDocs);
  if (allDocs.length === 0) return null;
  const supportedCount = allDocs.filter(canIncludeInThreadPdf).length;
  const unsupportedCount = allDocs.length - supportedCount;
  if (supportedCount === 0) {
    return (
      <div className="rounded-sm border border-border-default bg-base/30 px-3 py-2 text-xs text-tertiary">
        No PDF/JPG/PNG attachments are available to combine for this thread.
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-sm border border-border-default bg-base/30 px-3 py-2 text-xs">
      <a
        href={threadAttachmentsPdfUrl(threadId)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 rounded-sm border border-border-default px-2 py-1 text-secondary hover:border-border-strong hover:text-primary"
      >
        <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
        Download all attachments PDF
      </a>
      <span className="text-tertiary">
        Includes {supportedCount} PDF/JPG/PNG file{supportedCount === 1 ? '' : 's'}
        {unsupportedCount > 0 ? `; ${unsupportedCount} unsupported file${unsupportedCount === 1 ? '' : 's'} listed in the PDF` : ''}.
      </span>
    </div>
  );
}

function commIcon(t: CommType) {
  switch (t) {
    case 'PHONE_CALL':
      return Phone;
    case 'MEETING':
      return Calendar;
    case 'LETTER_RECEIVED':
      return ArrowDown;
    case 'LETTER_SENT':
      return Send;
    case 'EMAIL':
      return Mail;
    case 'WHATSAPP':
    case 'SMS':
      return MessageSquare;
    case 'INTERNAL_NOTE':
      return NotebookPen;
  }
}

export function AttachmentDetailDrawer({ attachmentId, onClose }: Props) {
  const me = useCurrentUser();
  const att = useAttachment(attachmentId ?? undefined);
  const del = useDeleteAttachment();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const data = att.data;
  const isOwn = data && me.data && data.createdByUserId === me.data.user.id;
  const isAdmin = me.data?.user.role === 'ADMIN';
  const canDelete = Boolean(isOwn || isAdmin);

  return (
    <Drawer
      open={!!attachmentId}
      onClose={onClose}
      title={
        data ? (
          data.kind === 'DOCUMENT' ? (
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-tertiary" strokeWidth={1.5} />
              <span className="truncate">{data.displayName}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {(() => {
                const Icon = commIcon(data.commType ?? 'INTERNAL_NOTE');
                return <Icon className="h-4 w-4 text-tertiary" strokeWidth={1.5} />;
              })()}
              <span className="truncate">{data.subject}</span>
            </div>
          )
        ) : (
          'Attachment'
        )
      }
      footer={
        data ? (
          <div className="flex items-center gap-2">
            {data.kind === 'DOCUMENT' && data.downloadUrl ? (
              <a
                href={data.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-sm border border-border-default px-3 py-1.5 text-sm text-secondary hover:border-border-strong hover:text-primary"
              >
                <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
                Download
              </a>
            ) : null}
            <div className="ml-auto" />
            {canDelete ? (
              confirmingDelete ? (
                <div className="inline-flex items-center gap-1.5 text-xs">
                  <span className="text-secondary">Delete?</span>
                  <Button
                    variant="destructive"
                    size="compact"
                    onClick={() => {
                      if (!data) return;
                      del.mutate(data.id, {
                        onSuccess: () => {
                          setConfirmingDelete(false);
                          onClose();
                        },
                      });
                    }}
                  >
                    {del.isPending ? 'Deleting…' : 'Yes, delete'}
                  </Button>
                  <Button variant="tertiary" size="compact" onClick={() => setConfirmingDelete(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  variant="tertiary"
                  size="compact"
                  onClick={() => setConfirmingDelete(true)}
                  className="text-status-danger hover:bg-status-danger-bg"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Delete
                </Button>
              )
            ) : null}
          </div>
        ) : null
      }
    >
      {att.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spinner className="h-5 w-5 text-tertiary" />
        </div>
      ) : att.isError ? (
        <Alert tone="danger">Could not load this attachment.</Alert>
      ) : data ? (
        <div className="space-y-4">
          {data.kind === 'DOCUMENT' ? (
            <DocumentBody data={data} />
          ) : (
            <CommunicationBody data={data} />
          )}
        </div>
      ) : null}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Document body
// ---------------------------------------------------------------------------

function DocumentBody({ data }: { data: Attachment }) {
  const mime = data.fileMimeType ?? '';
  const isPdf = mime === 'application/pdf';
  const isImage = mime.startsWith('image/');
  return (
    <>
      {data.downloadUrl && (isPdf || isImage) ? (
        <div className="overflow-hidden rounded-sm border border-border-default bg-base/40">
          {isPdf ? (
            <iframe
              src={data.downloadUrl}
              title={data.displayName ?? 'Document preview'}
              className="h-80 w-full"
            />
          ) : (
            <img
              src={data.downloadUrl}
              alt={data.displayName ?? 'Document preview'}
              className="max-h-80 w-full object-contain"
            />
          )}
        </div>
      ) : (
        <div className="rounded-sm border border-border-default bg-base/40 p-4 text-xs text-secondary">
          Preview not available. Use the download button to view this file.
        </div>
      )}

      <MetadataGrid
        rows={[
          ['Document date', formatDate(data.documentDate)],
          ['Type', data.documentType ? DOC_TYPE_LABELS[data.documentType] : '—'],
          ['Size', data.fileSizeBytes ? humanSize(data.fileSizeBytes) : '—'],
          ['Uploaded', `${data.createdByDisplayName} · ${formatRelative(data.createdAt)}`],
        ]}
      />

      {/* Patch 3 — version timeline. The hook fetches the chain; the
        * section renders nothing for singletons (the common case of a doc
        * never replaced). */}
      <SupersedeChainSection anchorId={data.id} />

      {data.description ? (
        <section>
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">
            Description
          </h4>
          <p className="mt-1 whitespace-pre-wrap text-sm text-primary">{data.description}</p>
        </section>
      ) : null}

      {data.tags.length > 0 ? (
        <section>
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">Tags</h4>
          <div className="mt-1 flex flex-wrap gap-1">
            {data.tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center rounded-sm bg-accent/10 px-1.5 py-0.5 text-xs text-accent-strong"
              >
                #{t}
              </span>
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}

function SupersedeChainSection({ anchorId }: { anchorId: string }) {
  const chain = useSupersedeChain(anchorId);

  // Loading: don't show anything (the rest of the drawer is already useful).
  if (chain.isLoading || !chain.data) return null;
  const { versions, truncated } = chain.data;

  // Singletons (no supersede in either direction) — skip the section
  // entirely so we don't show a one-item timeline.
  if (versions.length <= 1) return null;

  return (
    <section>
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">
        Version history
      </h4>
      <ol className="mt-1.5 space-y-1.5">
        {versions.map((v, i) => {
          const a = v.attachment;
          const versionLabel = `v${i + 1}`;
          return (
            <li
              key={a.id}
              className={
                'flex items-center gap-2 rounded-sm border px-2 py-1.5 text-xs ' +
                (v.isAnchor
                  ? 'border-accent bg-accent/5'
                  : 'border-border-default bg-base/40')
              }
            >
              <span className="shrink-0 font-mono text-tertiary">{versionLabel}</span>
              <span className="min-w-0 flex-1 truncate text-primary">
                {v.isDeleted ? (
                  <span className="italic text-tertiary line-through">
                    {a.displayName ?? 'Document'} (deleted)
                  </span>
                ) : (
                  a.displayName ?? '—'
                )}
              </span>
              <span className="shrink-0 text-tertiary">
                {a.documentDate ? formatDate(a.documentDate) : formatRelative(a.createdAt)}
              </span>
              {v.isAnchor ? (
                <span className="shrink-0 rounded-sm bg-accent/10 px-1 py-0.5 text-[10px] font-medium text-accent-strong">
                  current view
                </span>
              ) : !v.isDeleted && a.downloadUrl ? (
                <a
                  href={a.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-tertiary hover:text-accent-strong"
                  aria-label={`Download ${versionLabel}`}
                  title="Download this version"
                >
                  <Download className="h-3 w-3" strokeWidth={1.5} />
                </a>
              ) : null}
            </li>
          );
        })}
      </ol>
      {truncated ? (
        <p className="mt-1 text-[10px] text-tertiary">
          Chain truncated at 10 versions. Some older or newer revisions aren't shown.
        </p>
      ) : null}
    </section>
  );
}

function CommunicationBody({ data }: { data: Attachment }) {
  // Each comm has a thread; pull all messages chronologically.
  const thread = useThread(data.threadId ?? undefined);
  const [replyParentId, setReplyParentId] = useState<string | null>(null);

  // Multi-message thread tree. MUST be computed before the early returns so
  // the hook order stays stable across renders — putting useMemo after a
  // conditional return is exactly what produces "Rendered more hooks than
  // during the previous render". Guard the dependency: an empty list is a
  // valid "no data yet" signal that buildThreadTree handles cleanly.
  const tree = useMemo(
    () => buildThreadTree(thread.data?.messages ?? []),
    [thread.data?.messages],
  );

  // Single-message thread fast path: no chain UI, just render the body.
  const isSingle = thread.data && thread.data.messages.length <= 1;

  if (thread.isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Spinner className="h-4 w-4 text-tertiary" />
      </div>
    );
  }
  if (thread.isError || !thread.data) {
    return <SingleCommBody data={data} />;
  }
  if (isSingle) {
    return (
      <>
        {data.threadId ? <ThreadPdfDownloadLink threadId={data.threadId} messages={thread.data.messages} /> : null}
        <SingleCommBody data={data} />
        <div className="pt-2">
          <Button
            variant="tertiary"
            size="compact"
            onClick={() => setReplyParentId(data.id)}
          >
            <MessageCircleReply className="h-3.5 w-3.5" strokeWidth={1.5} />
            Reply
          </Button>
        </div>
        {replyParentId ? (
          <LogCommunicationModal
            open={true}
            onClose={() => setReplyParentId(null)}
            projectId={data.projectId ?? null}
            replyToCommId={replyParentId}
          />
        ) : null}
      </>
    );
  }

  // Multi-message thread — build a reply tree and render recursively.
  // Patch 4: previously this rendered a flat chronological list which lied
  // about reply relationships in branching threads. We now use in_reply_to_id
  // to build the actual tree and render it with depth-based indentation.

  return (
    <>
      <p className="text-xs text-secondary">
        {thread.data.messages.length} messages — oldest first
      </p>
      <ThreadPdfDownloadLink threadId={thread.data.threadId} messages={thread.data.messages} />
      <div className="space-y-3">
        {tree.map((node) => (
          <ThreadTreeNode
            key={node.message.id}
            node={node}
            depth={0}
            onReply={(id) => setReplyParentId(id)}
          />
        ))}
      </div>

      <div className="border-t border-border-default pt-3">
        <Button
          variant="primary"
          size="compact"
          onClick={() =>
            setReplyParentId(
              thread.data!.messages[thread.data!.messages.length - 1]!.message.id,
            )
          }
        >
          <MessageCircleReply className="h-3.5 w-3.5" strokeWidth={1.5} />
          Reply to thread
        </Button>
      </div>

      {replyParentId ? (
        <LogCommunicationModal
          open={true}
          onClose={() => setReplyParentId(null)}
          projectId={data.projectId ?? null}
          replyToCommId={replyParentId}
        />
      ) : null}
    </>
  );
}

function ThreadTreeNode({
  node,
  depth,
  onReply,
}: {
  node: ThreadNode;
  depth: number;
  onReply: (id: string) => void;
}) {
  // Cap depth-based indentation to avoid runaway nesting in deep chains.
  // The visual indent is per-level up to 4; beyond that we still nest in the
  // DOM but stop adding horizontal space so the cards stay readable.
  const indentPx = Math.min(depth, 4) * 16;
  const isReply = depth > 0;
  return (
    <>
      <div
        style={{ marginLeft: `${indentPx}px` }}
        className={
          // Left-border connector for non-root nodes — visually anchors a
          // reply to its parent the way email threading apps do.
          isReply
            ? 'border-l-2 border-border-default pl-3'
            : ''
        }
      >
        {isReply ? (
          <div className="flex items-center gap-2 pb-1 text-[10px] uppercase tracking-wide text-tertiary">
            <span className="h-3 w-px bg-border-default" />
            ▼ in reply
          </div>
        ) : null}
        {node.message.deletedAt ? (
          <ThreadMessageDeletedPlaceholder data={node.message} />
        ) : (
          <ThreadMessageCard
            data={node.message}
            attachedDocs={node.attachedDocs}
            onReply={() => onReply(node.message.id)}
          />
        )}
      </div>
      {node.children.length > 0 ? (
        <div className="mt-3 space-y-3">
          {node.children.map((child) => (
            <ThreadTreeNode
              key={child.message.id}
              node={child}
              depth={depth + 1}
              onReply={onReply}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

/**
 * Patch 4 — placeholder for soft-deleted comms in a thread. The spec promised
 * "deleted messages render as placeholders so the chain reads intact". The
 * thread endpoint already includes deleted rows; this component renders them
 * without exposing the body content, only enough to keep the timeline clear.
 */
function ThreadMessageDeletedPlaceholder({ data }: { data: Attachment }) {
  return (
    <article className="rounded-sm border border-border-default border-dashed bg-base/20 p-3 text-xs italic text-tertiary">
      <div className="flex items-center gap-2">
        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
        <span>Message deleted</span>
        <span className="ml-auto not-italic">
          {data.occurredAt ? formatDateTime(data.occurredAt) : ''}
        </span>
      </div>
    </article>
  );
}

function ThreadMessageCard({
  data,
  attachedDocs,
  onReply,
}: {
  data: Attachment;
  attachedDocs: Attachment[];
  onReply: () => void;
}) {
  const direction = data.direction;
  const Icon = commIcon(data.commType ?? 'INTERNAL_NOTE');
  const arrow =
    direction === 'INBOUND' ? '↙' : direction === 'OUTBOUND' ? '↗' : '—';
  return (
    <article className="rounded-sm border border-border-default bg-base/40 p-3 text-xs">
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-tertiary" strokeWidth={1.5} />
        <span className="text-secondary">{arrow}</span>
        <span className="font-medium text-primary">
          {data.counterpartyName ?? '—'}
        </span>
        <span className="text-tertiary">·</span>
        <span className="text-secondary">
          {direction === 'INBOUND'
            ? 'Inbound'
            : direction === 'OUTBOUND'
              ? 'Outbound'
              : 'Internal'}
        </span>
        <span className="ml-auto text-tertiary">
          {data.occurredAt ? formatDateTime(data.occurredAt) : ''}
        </span>
      </div>

      <h4 className="mt-1 text-sm font-medium text-primary">{data.subject}</h4>

      {data.referenceNumber || data.communicationDepartmentName || data.copiedDepartments.length > 0 ? (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-tertiary">
          {data.referenceNumber ? <span>Ref: {data.referenceNumber}</span> : null}
          {data.communicationDepartmentName ? (
            <span>Department: {data.communicationDepartmentName}</span>
          ) : null}
          {data.copiedDepartments.length > 0 ? (
            <span>Copied: {copiedDepartmentText(data)}</span>
          ) : null}
        </div>
      ) : null}

      {data.notes ? (
        <p className="mt-1 whitespace-pre-wrap text-secondary">{data.notes}</p>
      ) : null}

      {attachedDocs.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {attachedDocs.map((d) => (
            <li
              key={d.id}
              className="flex items-center gap-1 rounded-sm bg-surface px-2 py-1"
            >
              <FileText className="h-3 w-3 text-tertiary" strokeWidth={1.5} />
              <span className="truncate text-primary">{d.displayName}</span>
              {d.downloadUrl ? (
                <a
                  href={d.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto text-tertiary hover:text-accent-strong"
                >
                  <Download className="h-3 w-3" strokeWidth={1.5} />
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {data.tags.length > 0 ? (
        <div className="mt-1 flex flex-wrap gap-1">
          {data.tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center rounded-sm bg-accent/10 px-1.5 py-0.5 text-[10px] text-accent-strong"
            >
              #{t}
            </span>
          ))}
        </div>
      ) : null}

      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={onReply}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-tertiary hover:text-accent-strong"
        >
          <MessageCircleReply className="h-3 w-3" strokeWidth={1.5} />
          Reply
        </button>
      </div>
    </article>
  );
}

/** Original single-comm body (kept for documents-only flows and fallback). */
function SingleCommBody({ data }: { data: Attachment }) {
  const direction = data.direction;
  return (
    <>
      <MetadataGrid
        rows={[
          ['Type', data.commType ? COMM_TYPE_LABELS[data.commType] : '—'],
          ['Direction', direction === 'INBOUND' ? 'Inbound' : direction === 'OUTBOUND' ? 'Outbound' : 'Internal'],
          ['When', formatDateTime(data.occurredAt)],
          ['Counterparty', data.counterpartyName ?? '—'],
          ['Reference no.', data.referenceNumber ?? '—'],
          ['Department', data.communicationDepartmentName ?? '—'],
          ['Copied to', copiedDepartmentText(data)],
          ['Logged by', `${data.createdByDisplayName} · ${formatRelative(data.createdAt)}`],
        ]}
      />

      {data.notes ? (
        <section>
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">Notes</h4>
          <p className="mt-1 whitespace-pre-wrap text-sm text-primary">{data.notes}</p>
        </section>
      ) : null}

      {data.tags.length > 0 ? (
        <section>
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-tertiary">Tags</h4>
          <div className="mt-1 flex flex-wrap gap-1">
            {data.tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center rounded-sm bg-accent/10 px-1.5 py-0.5 text-xs text-accent-strong"
              >
                #{t}
              </span>
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}

function MetadataGrid({ rows }: { rows: [string, React.ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-xs">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="font-semibold uppercase tracking-wide text-tertiary">{label}</dt>
          <dd className="text-secondary">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
